import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaHeart, FaWater, FaGem, FaBed, FaCalendarAlt, FaUtensils, FaMountain, FaMapMarkerAlt, FaQuoteLeft } from 'react-icons/fa';

const About = () => {
  const values = [
    {
      icon: FaHeart,
      title: 'Authentic Hospitality',
      description: 'Warm Nepali hospitality meets modern luxury, creating genuine connections with every guest.'
    },
    {
      icon: FaWater,
      title: 'Lakeside Serenity',
      description: 'Breathtaking views of Phewa Lake and the Annapurna mountains from every room and common area.'
    },
    {
      icon: FaGem,
      title: 'Unforgettable Experiences',
      description: 'Curated moments that blend local culture, adventure, and relaxation into lasting memories.'
    },
    {
      icon: FaBed,
      title: 'Modern Comfort',
      description: 'Contemporary amenities in traditional surroundings, ensuring the perfect balance of both worlds.'
    }
  ];

  const timeline = [
    {
      year: '2010',
      event: 'Founded with vision to showcase Pokhara\'s beauty'
    },
    {
      year: '2015',
      event: 'Expanded to 50 luxurious rooms with lake views'
    },
    {
      year: '2018',
      event: 'Recognized as Top Hotel in Pokhara by TripAdvisor'
    },
    {
      year: '2021',
      event: 'Sustainability initiatives and eco-friendly practices'
    }
  ];

  const reasons = [
    { icon: FaMountain, text: 'Stunning mountain and lake views from every room' },
    { icon: FaUtensils, text: 'Rooftop dining with authentic Nepali and international cuisine' },
    { icon: FaMapMarkerAlt, text: 'Prime location near Phewa Lake and adventure activities' },
    { icon: FaGem, text: 'Curated cultural experiences and local artisan partnerships' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-highlight via-background to-background">
      {/* Intro Section */}
      <motion.section
        className="py-24 bg-gradient-to-r from-background to-highlight"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.h1
            className="text-5xl md:text-6xl font-serif font-bold text-primary mb-6"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            Our Story
          </motion.h1>
          <motion.p
            className="text-xl md:text-2xl text-secondary font-light"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Blending tradition, comfort, and the beauty of Pokhara.
          </motion.p>
        </div>
      </motion.section>

      {/* Two-Column Story Layout */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div>
                <h2 className="text-3xl font-serif font-bold text-primary mb-6">A Vision Born from Nature</h2>
                <p className="text-lg text-secondary leading-relaxed mb-6">
                  Hotel Admire Pokhara emerged from a simple yet profound vision: to create a sanctuary where the breathtaking beauty of Nepal's natural wonders meets unparalleled hospitality. Founded with the belief that true luxury lies in harmony with one's surroundings.
                </p>
                <p className="text-lg text-secondary leading-relaxed mb-6">
                  Our location at the edge of Phewa Lake positions us perfectly to offer guests the most serene and spectacular views of the Annapurna mountain range, creating an atmosphere where relaxation and inspiration coexist.
                </p>
              </div>
              <div>
                <h3 className="text-2xl font-serif font-bold text-primary mb-4">Cultural Authenticity</h3>
                <p className="text-lg text-secondary leading-relaxed">
                  We honor Nepali traditions while embracing contemporary comfort. From our locally sourced ingredients to our handcrafted furnishings, every element reflects our commitment to preserving and celebrating the rich cultural heritage of Pokhara and its surrounding regions.
                </p>
              </div>
            </motion.div>
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative">
                <motion.img
                  src={`${process.env.PUBLIC_URL}/assets/about2.png`}
                  alt="Hotel view"
                  className="w-full h-96 object-cover rounded-2xl shadow-2xl mb-6"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                />
                <motion.img
                  src={`${process.env.PUBLIC_URL}/assets/about1.png`}
                  alt="Lake view"
                  className="absolute -bottom-8 -left-8 w-48 h-36 object-cover rounded-xl shadow-xl border-4 border-white"
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  transition={{ duration: 0.3 }}
                />
                <motion.img
                  src={`${process.env.PUBLIC_URL}/assets/about3.png`}
                  alt="Mountain view"
                  className="absolute -top-8 -right-8 w-40 h-28 object-cover rounded-lg shadow-xl border-4 border-white"
                  whileHover={{ scale: 1.05, rotate: -2 }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-gradient-to-r from-highlight to-background">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-serif font-bold text-primary mb-4">Our Essence</h2>
            <p className="text-xl text-secondary max-w-2xl mx-auto">
              The core values that define every moment of your stay
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="bg-background p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 text-center group"
                whileHover={{ y: -10 }}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
              >
                <motion.div
                  className="text-5xl text-accent mx-auto mb-6 group-hover:scale-110 transition-transform duration-300"
                  whileHover={{ rotate: 5 }}
                >
                  <value.icon />
                </motion.div>
                <h3 className="text-xl font-serif font-bold text-primary mb-4">{value.title}</h3>
                <p className="text-secondary leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      
      {/* Why Choose Us */}
      <section className="py-24 bg-primary text-background">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6">Why Guests Love Us</h2>
              <p className="text-xl text-background mb-8 leading-relaxed">
                Every detail is crafted to create extraordinary experiences that linger in your memory long after your stay.
              </p>
            </motion.div>
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {reasons.map((reason, index) => (
                <motion.div
                  key={index}
                  className="flex items-start bg-background/10 backdrop-blur-sm p-6 rounded-xl"
                  whileHover={{ scale: 1.02 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <reason.icon className="text-3xl text-accent mr-4 mt-1 flex-shrink-0" />
                  <p className="text-lg leading-relaxed">{reason.text}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-r from-background to-highlight">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            className="bg-background p-12 rounded-3xl shadow-2xl"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <FaQuoteLeft className="text-4xl text-accent mx-auto mb-6" />
            <blockquote className="text-2xl md:text-3xl font-serif font-light text-secondary italic mb-8 leading-relaxed">
              "At Hotel Admire Pokhara, every stay is a memory in the making."
            </blockquote>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <a
                href="https://www.booking.com/hotel/np/admire-pokhara-pvt-ltd.html?aid=2405612&label=brave_nonbrand_organic_trigger_f3f828ac-4c6a-4b54-8e46-e2779b841553&sid=cd36b88c5418b460034b77c1a1c04aa8&all_sr_blocks=467069401_150918546_2_42_0&checkin=2025-12-08&checkout=2025-12-09&dest_id=-1022488&dest_type=city&dist=0&group_adults=2&group_children=0&hapos=1&highlighted_blocks=467069401_150918546_2_42_0&hpos=1&matching_block_id=467069401_150918546_2_42_0&no_rooms=1&req_adults=2&req_children=0&room1=A%2CA&sb_price_type=total&sr_order=popularity&sr_pri_blocks=467069401_150918546_2_42_0__1031&srepoch=1765217458&srpvid=fa2b7fd5ce9900a3&type=total&ucfs=1&#RD467069403"
                target='_blank'
                className="inline-block bg-accent hover:bg-accent/90 text-primary font-serif font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Your Stay
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;